import Link from '@tiptap/extension-link';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import type { Editor, Extensions } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { blockTextToTipTapContent, tipTapHtmlToBlockText } from '../utils/richText';

type EmailBlockTipTapProps = {
  blockId: string;
  content: string;
  blockType: 'text' | 'title';
  style: CSSProperties;
  disabled?: boolean;
  onChange: (html: string) => void;
  onEditorReady?: (editor: Editor | null) => void;
};

function createExtensions(blockType: 'text' | 'title'): Extensions {
  return [
    StarterKit.configure({
      heading: blockType === 'title' ? { levels: [2] } : false,
      blockquote: false,
      codeBlock: false,
      horizontalRule: false,
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      autolink: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
    TextStyleKit,
  ];
}

export function EmailBlockTipTap({
  blockId,
  content,
  blockType,
  style,
  disabled,
  onChange,
  onEditorReady,
}: EmailBlockTipTapProps) {
  const lastSyncedContent = useRef(content);
  const extensions = useMemo(() => createExtensions(blockType), [blockType]);

  const editor = useEditor({
    extensions,
    content: blockTextToTipTapContent(content, blockType),
    editable: !disabled,
    autofocus: false,
    onUpdate: ({ editor: currentEditor }) => {
      const html = tipTapHtmlToBlockText(currentEditor.getHTML(), blockType);
      lastSyncedContent.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    lastSyncedContent.current = content;
  }, [blockId]);

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || content === lastSyncedContent.current) {
      return;
    }
    lastSyncedContent.current = content;
    editor.commands.setContent(blockTextToTipTapContent(content, blockType), { emitUpdate: false });
  }, [editor, content, blockType]);

  useEffect(() => {
    if (!editor || !onEditorReady) {
      return undefined;
    }

    onEditorReady(editor);
    return () => onEditorReady(null);
  }, [editor, onEditorReady]);

  return (
    <div className='ee-tiptap' style={style}>
      <EditorContent editor={editor} />
    </div>
  );
}
